package nl.volleyplanner.web.base

import kotlinx.browser.document
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.dom.clear
import kotlinx.html.dom.create
import kotlinx.html.js.div
import nl.volleyplanner.api.auth.AuthLevel
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.base.component.BreadcrumbComponent
import nl.volleyplanner.web.base.routing.PageMountContext
import nl.volleyplanner.web.base.routing.PageRouteContext
import nl.volleyplanner.web.base.routing.PageRouteController
import nl.volleyplanner.web.base.routing.RouteChange
import org.w3c.dom.HTMLElement

/**
 * Generic controller for anything to display
 *
 * For pages: @see PageController instead
 */
abstract class Controller {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.Controller")

    abstract val name: String

    private val components = mutableSetOf<HTMLComponent>()
    private val simpleComponents = mutableSetOf<SimpleComponent>()
    private val uninitializedComponents = mutableSetOf<SimpleComponent>()

    protected var rootElement: HTMLElement? = null
    private val repaintMutex = Mutex()

    suspend fun mount(rootElement: HTMLElement) {
        this.rootElement = rootElement
        logger.debug { "[$name] Mounted" }
        repaint()
    }

    suspend fun repaint() = repaintMutex.withLock {
        logger.debug { "[$name] Repaint requested" }
        // TODO: Move towards local render scope
        CoroutineManager.newRenderScope()
        ControllerContext.withController(this) {
            dispose()
            rootElement!!.clear()
            val result = doRender()
            rootElement!!.append(result)
            onReady()
            initializeSimpleComponents()
        }
        logger.debug { "[$name] Repaint complete" }
    }

    suspend fun unmount() {
        logger.debug { "[$name] unmount" }
        dispose()
    }

    fun hasComponent(component: HTMLComponent) : Boolean {
        return components.contains(component)
    }

    fun registerComponent(component: HTMLComponent) : Boolean {
        return components.add(component)
    }

    fun unregisterComponent(component: HTMLComponent) : Boolean {
        return components.remove(component)
    }

    fun registerSimpleComponent(simpleComponent: SimpleComponent) {
        if (simpleComponents.add(simpleComponent)) uninitializedComponents.add(simpleComponent)
    }

    open fun dispose() {
        logger.debug("dispose ${this::class.simpleName} having ${components.size} components and ${simpleComponents.size} simple components")
        simpleComponents.forEach { it.dispose() }
        simpleComponents.clear()
        uninitializedComponents.clear()
        logger.trace("Dispose components")
        val componentsToDispose = components.toList()
        componentsToDispose.forEach { it.dispose() }
        logger.trace("Clear components")
        components.clear()
        logger.trace("Dispose complete")
    }

    suspend fun doRender() : HTMLElement {
        // Likely we'll rebuild the entire thing. So any existing component will be rebuild as well
        dispose()

        return render()
    }

    fun initializeSimpleComponents() {
        logger.trace("$name: Initializing ${uninitializedComponents.size} simple components")
        uninitializedComponents.forEach { component -> component.onReady() }
        uninitializedComponents.clear()
        // Quick hack. Should perhaps call this through a separate function
        // TODO: Why did we have this in the first place? Can we remove it? It does call the function twice
        //  sometimes when onContentReplaced is also necessary for a regular refresh
        //  2025-08-30: it seems we had this to trigger functions when simple components were available
        //  especially the assigneefinder. That scenario should now likely be fixed so we can remove this later
        // components.forEach { component -> component.onContentReplaced() }
    }

    open fun onReady() {}

    protected abstract suspend fun render() : HTMLElement
}

abstract class PageController : Controller(), PageRouteController {
    private val logger = Logger.getLogger("nl.volleyplanner.web.base.PageController")

    protected var _pageRouteContext: PageRouteContext? = null
    val pageRouteContext: PageRouteContext
        get() = _pageRouteContext!!

    override suspend fun mount(context: PageMountContext) {
        this._pageRouteContext = context.pageRouteContext
        mount(context.root)
    }

    override suspend fun update(change: RouteChange) {
        this._pageRouteContext = change.current
    }
}

/**
 * Controller for pages that can be routed towards
 */
abstract class WebPageController : PageController() {
    abstract val path: String
    abstract val requiredAuthLevel: AuthLevel
    abstract val parent: Controller?
    abstract val childPages: List<WebPageController>

    abstract suspend fun loadPageMeta(pageContext: PageRouteContext) : PageMeta

    fun getPageMeta(scope: CoroutineScope, pageContext: PageRouteContext) : Deferred<PageMeta> {
        return scope.async { loadPageMeta(pageContext) }
    }

    abstract suspend fun renderContent() : HTMLElement

    fun renderBreadcrumb() : HTMLElement? {
        val tree = mutableListOf<WebPageController>()
        tree.add(0, this)
        var parent: Controller? = parent
        while (parent != null) {
            if (parent is WebPageController) {
                tree.add(0, parent)
                parent = parent.parent
            } else {
                parent = null
            }
        }
        return if (tree.size > 1) {
            ComponentManager.createComponent(BreadcrumbComponent(tree, _pageRouteContext!!), document.create)
        } else null
    }

    final override suspend fun render(): HTMLElement {
        val breadcrumbElement = renderBreadcrumb()
        val contentElement = renderContent()
        val wrapper = document.create.div {}
        if (breadcrumbElement != null) {
            wrapper.append(breadcrumbElement)
        }
        wrapper.append(contentElement)
        return wrapper
    }
}

/**
 * Controller for 'static' pages that have no path parameters
 * Uses a title property to set the title for any navigational purposes
 */
abstract class StaticPageController : WebPageController() {
    abstract val title: String

    override suspend fun loadPageMeta(pageContext: PageRouteContext) = PageMeta(title, path)
}

/**
 * Controller for pages at the 'root' of the app
 * These should have a fixed title that can be used for navigation
 * The 'path' should not have any parameters
 *  - For parameterized paths, create different controllers with their parameters filled in
 */
abstract class RootPageController : StaticPageController() {
    override val parent: Controller? = null
}

object ControllerContext {
    private val controllerStack = mutableListOf<Controller>()

    /**
     * Ensures we are working with a single controller within the block
     * The block can create nested withController blocks, potentially with different controllers
     *
     * While within the block getController() will always return the controller that is valid within that block
     */
    suspend fun <T> withController(newController: Controller, block: suspend () -> T) : T {
        controllerStack.add(newController)

        return try {
            //console.log("Running withController: ${newController::class.simpleName}")
            block()
        } finally {
            val removedController = controllerStack.removeAt(controllerStack.size - 1)
            //console.log("Run withController completed: ${newController::class.simpleName}. ${controllerStack.size} controllers left")
        }
    }

    fun getController() : Controller? {
        return controllerStack.lastOrNull()
    }
}